import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ImagePreview from '@/components/common/media/ImagePreview';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { useListQueryParams } from '@/hooks/useListQueryParams';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  ICollection,
  ICollectionResponse,
  ICollectionState,
} from '@/interfaces/collection.interface';
import { ActionConfig, ICommonOption } from '@/interfaces/common.interface';
import {
  collectionOption,
  GetFilterUrl,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { PlusSquare } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Collection: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canEdit, canCreate } = usePermissions();

  // ✅ URL-driven params
  const { params, setParams } = useListQueryParams({
    isActive: '',
    search: '',
    sort: 'DESC',
  });

  const { page, limit, search, isActive, sort } = params;

  // ✅ State object (UI + API response only)
  const [state, setState] = useState<ICollectionState>({
    collection: [],
    pageInfo: {
      total: 1,
      totalPages: 1,
      limit: limit,
      page: page,
    },
    isLoading: false,
    selectedCollectionId: null,
    showUpdateModel: false,
    statusToUpdate: null,
  });

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.collection);

  const collectionFilters: ICommonOption[] = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ];

  const collectionColumns: TableColumn<ICollection>[] = [
    {
      key: 'title',
      header: 'Title',
      render: (collection) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${collection?.media?.[0]?.url}`}
          alt=""
          text={collection.title}
        />
      ),
    },
    {
      key: 'price',
      header: 'Price (BHD)',
      render: (collection) => toFixedThree(collection.price),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.isActive ? 'active' : 'inactive'}
          options={collectionOption}
          onChange={(status) =>
            setState((prev) => ({
              ...prev,
              showUpdateModel: true,
              selectedCollectionId: item.id,
              statusToUpdate: status,
            }))
          }
          canEditVal="collection"
        />
      ),
    },
    {
      key: 'products',
      header: 'Products',
      render: (order) => `${order.items.length}`,
    },
  ];

  const collectionActionConfig: Record<string, ActionConfig<ICollection>> = {
    view: {
      text: 'View',
      onClick: (collection) =>
        navigate(`${RoutesLink.collectionDetails}/${collection.id}`),
      permission: () => canView('collection'),
    },
    edit: {
      text: 'Edit',
      onClick: (collection) =>
        navigate(`${RoutesLink.createCollection}/${collection.id}`),
      permission: () => canEdit('collection'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(collectionActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit],
  );

  // 🔹 Fetch collections
  const fetchCollection = async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      const query = GetFilterUrl({
        page,
        limit,
        search,
        isActive,
        orderDir: sort,
      });

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.bundles}?${query}`;
      const res = await callApi<ICollectionResponse>(PATH, { method: 'GET' });

      setState((prev) => ({
        ...prev,
        collection: res.data,
        pageInfo: res.pageInfo,
        isLoading: false,
      }));
    } catch {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    fetchCollection();
  }, [page, limit, search, isActive, sort]);

  const onStatusUpdate = async () => {
    if (!state.selectedCollectionId || !state.statusToUpdate) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.bundles}/${state.selectedCollectionId}`,
        {
          method: 'PATCH',
          data: { isActive: state.statusToUpdate === 'active' },
        },
      );

      fetchCollection();
    } catch (err: any) {
      showToastNotification('danger', err.message);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const filterOption = [
    { value: 'DESC', label: 'Newest First' },
    { value: 'ASC', label: 'Oldest First' },
  ];

  return (
    <Layout>
      <PageHeading
        text="Collection"
        button={
          canCreate('collection')
            ? {
                icon: <PlusSquare />,
                text: 'Add Collection',
                onClick: () => navigate(RoutesLink.createCollection),
              }
            : undefined
        }
      />

      <Card>
        <CommonFilter
          activeFilter={isActive}
          filters={collectionFilters}
          inputPlaceHolder="Search for Collection..."
          searchValue={search}
          onSearch={(text) => setParams({ search: text })}
          onChange={(value) => setParams({ isActive: value })}
          filterOptions={{
            values: filterOption,
            onChange: (value) => setParams({ sort: value }),
          }}
        />

        <CommonTable
          columns={collectionColumns}
          data={state.collection}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Collections found"
          loading={state.isLoading}
          actions={actions}
        />

        {state.pageInfo.totalPages > 1 && (
          <Pagination
            paginationData={state.pageInfo}
            onPageChange={(p) => setParams({ page: p })}
            onPageSizeChange={(l) => setParams({ limit: l })}
          />
        )}

        {state.showUpdateModel && (
          <ConfirmationModel
            modelType="update"
            show
            subTitle={`Update status to ${state.statusToUpdate}`}
            onClose={() =>
              setState((prev) => ({
                ...prev,
                showUpdateModel: false,
                selectedCollectionId: null,
                statusToUpdate: null,
              }))
            }
            onConfirm={onStatusUpdate}
          />
        )}
      </Card>
    </Layout>
  );
};

export default Collection;
